import {connect} from "react-redux";
import {getMinutes, getMinutesChart} from "../../../../../../utils/constantValues";
import {ASTRO_DIM_5} from "../../../../../../utils/constantFeatures";
import {getErrorIssues, getWarningIssues} from "../../../../../../utils/validationSummary";
import {fetchDriverOpmodesSelected} from "../../../../../../actions";
import {fetchDriverProperties, fetchAstroSavings, fetchDriverDefaults} from "../../../../../../actions";
import {Trans} from 'react-i18next';

import * as d3 from "d3";
import moment from "moment";
import BaseFeature from "./BaseFeature";
import FeatureCard from "./components/FeatureCard";
import {Button} from "components/ui/button";
import {cn} from "lib/utils";
import PropertyDropdown from "./modern/PropertyDropdown";
import PropertyRadio from "./modern/PropertyRadio";
import DraggableAreaChart from "./charts/DraggableAreaChart";
import PropertyTableTimePicker10 from "./components/PropertyTableTimePicker10";
import {t} from "i18next";

const ASTRO_DIM_5_TIME_ORDER = [
    ASTRO_DIM_5.time2, ASTRO_DIM_5.time3, ASTRO_DIM_5.time4, ASTRO_DIM_5.time5,
    ASTRO_DIM_5.time6, ASTRO_DIM_5.time7, ASTRO_DIM_5.time8, ASTRO_DIM_5.time9, ASTRO_DIM_5.time10,
];

const ASTRO_DIM_5_LEVEL_ORDER = [
    ASTRO_DIM_5.outputLevel1, ASTRO_DIM_5.outputLevel2, ASTRO_DIM_5.outputLevel3, ASTRO_DIM_5.outputLevel4,
    ASTRO_DIM_5.outputLevel5, ASTRO_DIM_5.outputLevel6, ASTRO_DIM_5.outputLevel7, ASTRO_DIM_5.outputLevel8,
    ASTRO_DIM_5.outputLevel9, ASTRO_DIM_5.outputLevel10,
];

const ASTRO_DIM_5_FADE_ORDER = [
    ASTRO_DIM_5.switchOnFadeTime, ASTRO_DIM_5.astroDimFadeTime, ASTRO_DIM_5.switchOffFadeTime,
];

// Every value this page actually renders, submode aside: the three fade times and the reference
// schedule's levels and breakpoints. The ast5 feature also carries ast5.la / ast5.lo / ast5.ts and
// ast5.col1-8, which are deliberately left out - they have no control on this screen, so restoring
// them would change settings the user cannot see.
const ASTRO_DIM_5_PAGE_VALUES = [
    ...ASTRO_DIM_5_FADE_ORDER,
    ...ASTRO_DIM_5_LEVEL_ORDER,
    ...ASTRO_DIM_5_TIME_ORDER,
];

// Used to decide whether this project came from a device readback, by comparing each property's
// Default against the DD store's. Only format-stable properties belong here: the submode and the
// levels are plain scales and the time slots are timestamps converted verbatim, so the two sources
// always render them identically. The three fade times are deliberately excluded - converter.py
// builds them with createFeaRefEnumPropFromScale, so they are enums whose Convert2UserValue returns
// null when the default is not in the available-values list. That null-versus-label difference is
// not evidence of a readback, and treating it as such made the card appear on every project.
const ASTRO_DIM_5_READBACK_MARKERS = [
    ASTRO_DIM_5.submode,
    ...ASTRO_DIM_5_LEVEL_ORDER,
    ...ASTRO_DIM_5_TIME_ORDER,
];

class AstroDim5 extends BaseFeature {
    constructor(props) {
        super(props);

        this.state = {
            savings: "Calculating...",
            countryKey: "Custom",
        };

        this.handleChange = this.handleChange.bind(this);
        this.handleChangeCountry = this.handleChangeCountry.bind(this);
        this.handleCountryDropdownChange = this.handleCountryDropdownChange.bind(this);
        this.handleChangeReferenceSchedule = this.handleChangeReferenceSchedule.bind(this);
        this.restoreLoadedData = this.restoreLoadedData.bind(this);
    }

    handleChange(propertyName, value) {
        this.setState({savings: "Calculating..."});
        const v = value;

        if (propertyName) {
            this.props.handleChange(propertyName, v);
            // restoreLoadedData writes the submode itself and then restores the schedule that was
            // read from the device, so the usual reset-to-defaults must not run and overwrite it.
            if (this.getCorrectPropertyName(propertyName) === ASTRO_DIM_5.submode && !this.suppressSubmodeReset) {
                if (value === "0") {
                    this.handleChangeCountry("Custom");
                    this.resetTimeSlotsToDefault();
                } else if (value === "1") {
                    this.applyTimeBasedDefaultSpacing();
                }
            }
        }

        setTimeout(() => {
            this.props.fetchAstroSavings(this.props.project.id, this.props.driver.driverId)
                .then((e) => {
                    const value = this.props.astroSavingsValue;
                    this.setState({savings: value + "%"});
                });
        },
        2500
        );
    }

    getReferenceScheduleAnchor() {
        // RTC_Based (submode "0") schedules start at 13:00; every other submode starts at 00:00,
        // matching the chart's existing minHourString convention.
        return this.props.properties[ASTRO_DIM_5.submode].value === "0" ? "13:00" : "00:00";
    }

    handleChangeReferenceSchedule(propertyName, value) {
        this.handleChange(propertyName, value);
        this.setState({countryKey: "Custom"});
    }

    handleCountryDropdownChange(e) {
        this.handleChangeCountry(e.target.name);
    }

    resetTimeSlotsToDefault() {
        // Factory defaults come from the DD store, not from the property's own valueMeta.default:
        // importing a production file overwrites that field with the value read off the device (the
        // format has a single value slot), so on an imported project it holds the readback schedule
        // rather than the factory one. valueMeta.default is only a fallback for the window before
        // the fetch resolves - it is correct on any project that was not created from a readback.
        const factory = this.props.factoryDefaults;
        ASTRO_DIM_5_TIME_ORDER.forEach((name) => {
            const value = factory && factory[name] !== undefined
                ? factory[name]
                : this.props.properties[name].valueMeta.default;
            this.props.handleChange(name, value);
        });
    }

    /**
     * True when this project was created from a device readback.
     *
     * There is no flag for it, but the import itself leaves a reliable trace: it writes the values
     * read off the driver into each property's Default, so those stop matching the DD store's
     * factory values. When the two agree there is nothing to restore and the action is a no-op, so
     * this doubles as the test for whether it is worth offering at all. Returns false until the
     * factory defaults have arrived, so the control does not appear before its state is known.
     */
    hasLoadedData() {
        const factory = this.props.factoryDefaults;
        if (!factory) {
            return false;
        }
        return ASTRO_DIM_5_READBACK_MARKERS.some((name) => {
            const loaded = this.props.properties[name]?.valueMeta?.default;
            const factoryValue = factory[name];
            // A missing value on either side proves nothing, so it must not count as a difference.
            if (loaded === undefined || loaded === null || factoryValue === undefined || factoryValue === null) {
                return false;
            }
            return String(loaded) !== String(factoryValue);
        });
    }

    /**
     * Puts every value on this page back to what was read from the device: submode, the three fade
     * times, and the reference schedule's levels and breakpoints.
     *
     * The readback lives in each property's valueMeta.default, because the import wrote it there.
     * If the file format is ever changed to carry a value and a default separately, this is the
     * call site that has to move to the new field.
     */
    restoreLoadedData() {
        const loaded = (name) => this.props.properties[name]?.valueMeta?.default;

        this.suppressSubmodeReset = true;
        try {
            // Submode first, so the table's anchor and range checks already match the times below.
            const submode = loaded(ASTRO_DIM_5.submode);
            if (submode !== undefined) {
                this.handleChange(ASTRO_DIM_5.submode, submode);
            }
            ASTRO_DIM_5_PAGE_VALUES.forEach((name) => {
                const value = loaded(name);
                if (value !== undefined) {
                    this.props.handleChange(name, value);
                }
            });
        } finally {
            this.suppressSubmodeReset = false;
        }

        this.setState({countryKey: "Custom"});
    }

    applyTimeBasedDefaultSpacing() {
        // Time-based has no DDF default of its own to fall back on (the DDF's single static
        // Default represents the RTC-based factory schedule), so hardcode an evenly-spaced (2h)
        // set of slot times here instead, just so the schedule isn't left bunched at 00:00.
        const TIME_BASED_DEFAULT_TIMES = ["02:00", "04:00", "06:00", "08:00", "10:00", "12:00", "14:00", "16:00", "18:00"];
        ASTRO_DIM_5_TIME_ORDER.forEach((name, i) => this.props.handleChange(name, TIME_BASED_DEFAULT_TIMES[i]));
    }

    handleChangeCountry(value) {
        this.setState({countryKey: value});

        if (value === "FR – Arrêté nuisances lumineuses") {
            this.props.handleChange(ASTRO_DIM_5.outputLevel1, "100");
            this.props.handleChange(ASTRO_DIM_5.outputLevel2, "70");
            this.props.handleChange(ASTRO_DIM_5.outputLevel3, "50");
            this.props.handleChange(ASTRO_DIM_5.outputLevel4, "50");
            this.props.handleChange(ASTRO_DIM_5.outputLevel5, "70");
            this.props.handleChange(ASTRO_DIM_5.time2, "16:30");
            this.props.handleChange(ASTRO_DIM_5.time3, "21:30");
            this.props.handleChange(ASTRO_DIM_5.time4, "07:00");
            this.props.handleChange(ASTRO_DIM_5.time5, "07:30");
        }
    }

    // Same error/warning signal as BaseFeature.getClassName, minus the legacy
    // .card-feature-component base class. `.danger` / `.warning` are standalone
    // border rules in stylesheet.css, so they still apply as-is.
    cardState(properties) {
        if (getErrorIssues(this.props.issues, properties).length > 0) return 'danger';
        if (getWarningIssues(this.props.issues, properties).length > 0) return 'warning';
        return '';
    }

    componentDidMount() {
        this.props.fetchDriverProperties(this.props.project.id, this.props.driver.driverId);
        // Fetched unconditionally rather than once per session: the map is keyed by property name
        // only, so a cached copy from a previously viewed driver would hand back the wrong defaults.
        this.props.fetchDriverDefaults(this.props.project.id, this.props.driver.driverId);
        const value = this.props.properties[ASTRO_DIM_5.energySavings]?.value || -1;
        this.setState({savings: value + "%"});
        this.handleChange(null, null);
    }

    render() {
        const isAsttm = this.props.opmodesSelected && (
            this.props.opmodesSelected.name === "ast-5" ||
            this.props.opmodesSelected.name === "astdimsens" ||
            this.props.opmodesSelected.name === "astroover"
        );
        const displayLocation = !isAsttm && this.props.properties[ASTRO_DIM_5.submode].value === "1";
        const submode = this.props.properties[ASTRO_DIM_5.submode];
        const switchOnFadeTime = this.props.properties[ASTRO_DIM_5.switchOnFadeTime];
        const switchOffFadeTime = this.props.properties[ASTRO_DIM_5.switchOffFadeTime];
        const astroDimFadeTime = this.props.properties[ASTRO_DIM_5.astroDimFadeTime];

        const dataChart = this.getDataChart(this.props.properties, submode);

        const minHourString = this.getReferenceScheduleAnchor();
        const maxHourString = submode.value === "0" ? "11:00" : "24:00";
        const minHour = this.getNumberFromHour(minHourString, 0, submode);
        const maxHour =
            submode.value === "0"
                ? this.getNumberFromHour(maxHourString, 0, submode)
                : moment(minHour).add(24, "hours").toDate();

        // RTC-based is conceptually the same as Astro-Based (a fixed daily restart, just clock-driven
        // instead of dusk/dawn-driven), so pad the view a matching 1h on both sides of the schedule's
        // own bounds rather than cramping the anchor right up against the axis edge.
        const min = {x: submode.value === "0" ? moment(minHour).subtract(1, "hours").toDate() : minHour};
        const max = {x: submode.value === "0" ? moment(maxHour).add(1, "hours").toDate() : maxHour, y: 100};

        // RTC-based ticks are literal clock hours (centered on the 13:00 restart). Time-based/SD
        // ticks are elapsed hours since the start of the schedule, not a time of day, so they're
        // labelled "+Nh" from that start rather than reusing the clock-hour format.
        const tickFormat = {
            x: submode.value === "0"
                ? d3.timeFormat("%H")
                : (d) => Math.round(moment(d).diff(moment(min.x), "hours", true)) + "h",
        };

        const time = [];
        const tickValues = {x: time};
        if (min.x && max.x) {
            for (const t = moment(min.x); t.toDate() <= max.x; t.add(1, "hours")) {
                time.push(t.toDate());
            }
        }

        // Generated generously (rather than a fixed-size literal) so it can never run out of
        // alternating light/dark sections regardless of how many fade-time transitions the
        // schedule produces.
        const sections = {};
        for (let i = 0; i <= 40; i++) {
            sections[i] = i % 2 === 0 ? "area-dark" : "area-light";
        }

        return (
            // Content centred with gutters on wide screens; the app shell is untouched.
            <div className="mx-auto w-full max-w-[1800px] px-4 pb-4">
                {/* Same grid and the same spans as AstroDimSensor on purpose: these two
                    features sit next to each other in the sidebar, and the Submode and
                    Fade Timing cards are the same content, so they must not change width
                    when you switch between them. */}
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-3 xl:grid-cols-12">

                    <FeatureCard
                        variant="modern"
                        bodyLayout="fields"
                        title={<Trans i18nKey="Submode"/>}
                        className={cn('lg:col-span-1 xl:col-span-2', this.cardState([submode]))}>
                        <PropertyRadio
                            property={submode}
                            isChecked={submode.value === "0"}
                            valueForTrue={"0"}
                            title={<Trans i18nKey="RTC_Based"/>}
                            handleChange={this.handleChange}
                        />
                        <PropertyRadio
                            disabled={submode.valueMeta.max === "0"}
                            property={submode}
                            isChecked={submode.value === "1"}
                            valueForTrue={"1"}
                            title={<Trans i18nKey="Time_Based"/>}
                            handleChange={this.handleChange}
                        />
                        <PropertyRadio
                            disabled={!this.props.opmodesSelected || !this.props.opmodesSelected.name.includes("pd")}
                            property={submode}
                            isChecked={submode.value === "2"}
                            valueForTrue={"2"}
                            title={<Trans i18nKey="SD_triggered"/>}
                            handleChange={this.handleChange}
                        />
                    </FeatureCard>

                    <FeatureCard
                        variant="modern"
                        bodyLayout="fields"
                        title={<Trans i18nKey="Fade_Timing"/>}
                        className={cn('lg:col-span-1 xl:col-span-3', this.cardState([astroDimFadeTime, switchOnFadeTime, switchOffFadeTime]))}>
                        <PropertyDropdown
                            property={switchOnFadeTime}
                            properties={switchOnFadeTime.valueMeta.availableValues.sort((a, b) => parseFloat(a) - parseFloat(b))}
                            propertyDisplayFunc={getMinutes}
                            title={<Trans i18nKey="Switch_On_Fade_Time"/>}
                            measureUnit={"mm:ss"}
                            min={switchOnFadeTime.valueMeta.min}
                            max={switchOnFadeTime.valueMeta.max}
                            handleChange={this.handleChange}
                        />
                        <PropertyDropdown
                            property={astroDimFadeTime}
                            properties={astroDimFadeTime.valueMeta.availableValues.sort((a, b) => parseFloat(a) - parseFloat(b))}
                            propertyDisplayFunc={getMinutes}
                            title={<Trans i18nKey="AstroDIM_Fade_Time"/>}
                            measureUnit={"mm:ss"}
                            min={astroDimFadeTime.valueMeta.min}
                            max={astroDimFadeTime.valueMeta.max}
                            handleChange={this.handleChange}
                        />

                        {submode.value === "0" ? (
                            <PropertyDropdown
                                property={switchOffFadeTime}
                                properties={switchOffFadeTime.valueMeta.availableValues.sort(
                                    (a, b) => parseFloat(a) - parseFloat(b)
                                )}
                                propertyDisplayFunc={getMinutes}
                                title={<Trans i18nKey="Switch_Off_Fade_Time"/>}
                                measureUnit={"mm:ss"}
                                min={switchOffFadeTime.valueMeta.min}
                                max={switchOffFadeTime.valueMeta.max}
                                handleChange={this.handleChange}
                            />
                        ) : null}
                    </FeatureCard>

                    {/* Only present on a project loaded from a device readback, and it sits beside
                        Fade Timing in the space that row already leaves free. Changing submode
                        replaces the schedule with that submode's defaults, so this is the way back
                        to what the driver actually had. */}
                    {this.hasLoadedData() ? (
                        <FeatureCard
                            variant="modern"
                            title={<Trans i18nKey="Loaded_Data"/>}
                            className="lg:col-span-1 xl:col-span-3">
                            <p className="text-sm text-muted-foreground">
                                <Trans i18nKey="Loaded_Data_Info"/>
                            </p>
                            <Button variant="outline" size="sm" onClick={this.restoreLoadedData}>
                                <Trans i18nKey="Reset_To_Loaded_Data"/>
                            </Button>
                        </FeatureCard>
                    ) : null}

                    <FeatureCard
                        variant="modern"
                        title={<Trans i18nKey="Reference_Schedule"/>}
                        className={cn('lg:col-span-3 xl:col-span-12', this.cardState(this.props.properties))}
                        // The 10-slot table cannot usefully reflow, so it scrolls inside its
                        // own card rather than forcing the page to overflow as the old
                        // minWidth:700px did.
                        bodyClassName="overflow-x-auto">
                        <PropertyTableTimePicker10
                            properties={this.props.properties}
                            extraProperties={this.props.extraProperties}
                            submodeProperty={ASTRO_DIM_5.submode}
                            handleChange={this.handleChangeReferenceSchedule}
                            sizeTitle={2}
                            sizePickers={2}
                            sizeMeasureUnit={2}
                            firstRow={{
                                title: <Trans i18nKey="Output_Level"/>,
                                property: ASTRO_DIM_5.outputLevel,
                                measureUnit: "%"
                            }}
                            secondRow={{
                                title: <Trans i18nKey="Time"/>,
                                property: ASTRO_DIM_5.time,
                                measureUnit: "hh:mm"
                            }}
                            displayColors={false}
                        />
                        <div className="pt-3 text-sm">
                            <Trans
                                i18nKey="Energy_Savings"/>: {submode.value === "2" || this.state.savings === "-1%" ? "N/A" : this.state.savings}
                        </div>
                    </FeatureCard>

                    <FeatureCard
                        variant="modern"
                        title={<Trans i18nKey="Graph"/>}
                        className="lg:col-span-3 xl:col-span-12">
                        <DraggableAreaChart
                            containsErrors={getErrorIssues(this.props.issues, this.props.properties).length > 0}
                            properties={this.props.properties}
                            dataChart={dataChart}
                            min={min}
                            max={max}
                            tickFormat={tickFormat}
                            tickValues={tickValues}
                            xThresholds={
                                displayLocation
                                    ? [{
                                        // Elapsed axis runs exactly [start, start+24h) here, so real midnight
                                        // sits midnightShift minutes after the start, not a whole day later.
                                        value: new Date(1970, 0, 1, 0, this.props.midnightShift, 0),
                                        color: "rgb(0,0,255)"
                                    }]
                                    : null
                            }
                            xLabels={this.getXLabels(dataChart, submode, displayLocation)}
                            x={{title: t(submode.value === "0" ? "Time_h" : "Time_Elapsed_h")}}
                            y={{title: t("Output_Level_Percent")}}
                            sections={sections}
                            handleChange={this.handleChartChange}
                        />
                    </FeatureCard>
                </div>
            </div>
        );
    }

    getXLabels(dataChart, submode, displayLocation) {
        const labels = [];
        const smallLabels = [];
        const ticks = [];

        if (submode.value === "2") {
            ticks.push(new Date(1970, 0, 1, 0, 0, 0));
            labels.push({label: "SD", x: new Date(1970, 0, 1, 0, 0, 0)});
        }

        if (displayLocation) {
            // Marks the real calendar midnight, which falls partway through the elapsed-time axis
            // rather than at its origin (the origin is the variable dusk-triggered start time).
            const midnight = new Date(1970, 0, 1, 0, this.props.midnightShift, 0);
            labels.push({label: t("Midnight"), x: midnight});
        }

        return {labels: labels, smallLabels: smallLabels, ticks: ticks};
    }

    getDataChart(properties, submode) {
        const dataChart = [];

        const minHourString = this.getReferenceScheduleAnchor();
        const maxHourString = submode.value === "0" ? "11:00" : "24:00";

        const minHour = this.getNumberFromHour(minHourString, 0, submode);
        const maxHour =
            submode.value === "0"
                ? this.getNumberFromHour(maxHourString, 0, submode)
                : moment(minHour).add(24, "hours").toDate();

        const offsetSwitchOnFadeTime = getMinutesChart(properties[ASTRO_DIM_5.switchOnFadeTime].value);
        const offsetAstroDimFadeTime = getMinutesChart(properties[ASTRO_DIM_5.astroDimFadeTime].value);
        const offsetSwitchOffFadeTime =
            submode.value === "0" ? getMinutesChart(properties[ASTRO_DIM_5.switchOffFadeTime].value) : "0";

        // The driver cannot output below its minimum dimming level, so the step from off up to that
        // minimum is instantaneous and the Switch On fade runs from there to the first level - which
        // is what makes the start-up transition a visible ramp. Same convention as AstroDim.jsx.
        const minDimming = properties[ASTRO_DIM_5.outputLevel1].valueMeta?.min ?? 0;

        let sectionCont = 0;

        if (submode.value !== "2") {
            dataChart.push({
                x: minHour,
                y: 0,
                section: sectionCont,
                color: "#32a852",
            });
            // No fading between 0 and the minimum dimming level.
            dataChart.push({
                x: minHour,
                y: minDimming,
                section: sectionCont,
                color: "#32a852",
            });
        } else {
            dataChart.push({
                x: new Date(1969, 11, 31, 22, 0, 0),
                y: 0,
                section: sectionCont,
                color: "#a6a832",
            });

            dataChart.push({
                x: new Date(1969, 11, 31, 22, offsetSwitchOnFadeTime, 0),
                y: properties[ASTRO_DIM_5.outputLevel1].value || 0,
                section: sectionCont,
                color: "#a6a832",
            });
        }

        const refArray = [
            {x: minHourString, y: properties[ASTRO_DIM_5.outputLevel1].value},
            {
                x: submode.value !== "2" ? properties[ASTRO_DIM_5.time2].value : "00:00",
                y: properties[ASTRO_DIM_5.outputLevel2].value,
            },
            {x: properties[ASTRO_DIM_5.time3].value, y: properties[ASTRO_DIM_5.outputLevel3].value},
            {x: properties[ASTRO_DIM_5.time4].value, y: properties[ASTRO_DIM_5.outputLevel4].value},
            {x: properties[ASTRO_DIM_5.time5].value, y: properties[ASTRO_DIM_5.outputLevel5].value},
            {x: properties[ASTRO_DIM_5.time6].value, y: properties[ASTRO_DIM_5.outputLevel6].value},
            {x: properties[ASTRO_DIM_5.time7].value, y: properties[ASTRO_DIM_5.outputLevel7].value},
            {x: properties[ASTRO_DIM_5.time8].value, y: properties[ASTRO_DIM_5.outputLevel8].value},
            {x: properties[ASTRO_DIM_5.time9].value, y: properties[ASTRO_DIM_5.outputLevel9].value},
            {x: properties[ASTRO_DIM_5.time10].value, y: properties[ASTRO_DIM_5.outputLevel10].value},
            {x: maxHourString, y: properties[ASTRO_DIM_5.outputLevel10].value},
        ];

        // Unlike RTC-based, these values are elapsed durations since the start (hard-capped at 24h
        // by the DDF), not absolute times-of-day, so there's no "next day" to wrap forward onto —
        // every point is plotted at face value on the single elapsed timeline.
        let firstPoint = 0;
        let lastPoint = refArray.length - 1;
        for (let i = 1; i < refArray.length - 1; i++) {
            const ref = refArray[i];
            const refValue = this.getNumberFromHour(ref.x, 0, submode);

            if (minHour > refValue) {
                firstPoint = i;
            }
            if (maxHour < refValue) {
                lastPoint = i;
            }
        }

        for (let i = firstPoint; i <= lastPoint; i++) {
            const ref = refArray[i];

            if (i === firstPoint) {
                dataChart.push({
                    x: this.getNumberFromHour(minHourString, submode.value !== "2" ? offsetSwitchOnFadeTime : 0, submode),
                    y: +ref.y || 0,
                    section: sectionCont,
                });
            } else if (i === lastPoint) {
                const prevRef = refArray[i - 1];

                // Start of the Switch Off ramp, clamped to the last point already plotted: a
                // schedule whose final step falls inside the fade window would otherwise put the
                // ramp's start before that step and fold the curve backwards, which hides the fade
                // instead of drawing it. Clamping shortens the ramp rather than losing it.
                const closingRampStart = submode.value === "0"
                    ? this.getNumberFromHour(maxHourString, -+offsetSwitchOffFadeTime, submode)
                    : maxHour;
                const lastPlotted = dataChart[dataChart.length - 1];

                dataChart.push({
                    x: new Date(Math.max(closingRampStart.getTime(), lastPlotted.x.getTime())),
                    y: +prevRef.y || 0,
                    section: sectionCont,
                });
            } else {
                const prevRef = refArray[i - 1];

                dataChart.push({
                    x: this.getNumberFromHour(ref.x, 0, submode),
                    y: +prevRef.y || 0,
                    section: sectionCont,
                });

                sectionCont++;

                dataChart.push({
                    x: this.getNumberFromHour(ref.x, offsetAstroDimFadeTime, submode),
                    y: +ref.y || 0,
                    section: sectionCont,
                });
            }
        }

        // Mirror of the start: the Switch Off fade has already brought the curve down to the minimum
        // dimming level, and the drop from there to off is instantaneous.
        dataChart.push({
            x: maxHour,
            y: minDimming,
        });

        dataChart.push({
            x: maxHour,
            y: 0,
        });

        return dataChart;
    }

    kelvinToHexTable(k) {
        const kTable = {
            1000: [255, 45, 0], //+
            1250: [255, 50, 5], //+
            1500: [255, 55, 10], //+
            1750: [255, 60, 15], //+
            2000: [255, 65, 20], //
            2250: [255, 90, 23], //+
            2500: [255, 115, 26], //+
            2750: [255, 140, 28], //+
            3000: [255, 166, 30], //
            3250: [255, 187, 48], //+
            3500: [255, 208, 66], //+
            3750: [255, 229, 84], //+
            4000: [255, 255, 103], //
            4250: [255, 255, 141], //+
            4500: [255, 255, 179], //+
            4750: [255, 255, 217], //+
            5000: [255, 255, 255], //
            5250: [239, 249, 255], //+
            5500: [226, 242, 255], //+
            5750: [213, 235, 255], //+
            6000: [200, 228, 255], //
            6250: [180, 216, 255], //+
            6500: [160, 206, 255], //+
            6750: [140, 196, 255], //+
            7000: [119, 186, 255], //
            7250: [107, 180, 255], //+
            7500: [97, 175, 255], //+
            7750: [87, 170, 255], //+
            8000: [77, 165, 255], //
            8250: [75, 160, 255],
            8500: [70, 155, 255],
            8750: [65, 150, 255],
            9000: [60, 145, 255],
            9250: [55, 140, 255],
            9500: [50, 135, 255],
            9750: [45, 130, 255],
            10000: [40, 125, 255],
        };

        // find the constant number
        const numbers = [];
        for (let i = 1000; i <= 10000; i += 250) {
            numbers.push(i);
        }

        let closestNumber = numbers[0];
        let smallestDifference = Math.abs(k - closestNumber);

        for (let i = 1; i < numbers.length; i++) {
            const currentNumber = numbers[i];
            const currentDifference = Math.abs(k - currentNumber);

            if (currentDifference < smallestDifference) {
                smallestDifference = currentDifference;
                closestNumber = currentNumber;
            }
        }
        return this.rgbToHex(kTable[closestNumber][0], kTable[closestNumber][1], kTable[closestNumber][2]);
    }

    rgbToHex(r, g, b) {
        return "#" + this.componentToHex(r) + this.componentToHex(g) + this.componentToHex(b);
    }

    componentToHex(c) {
        var hex = c.toString(16);
        return hex.length === 1 ? "0" + hex : hex;
    }

    getNumberFromHour(time, adding, submode, dayOffset) {
        const momentDate = moment("1970/01/01 " + time, "YYYY/MM/DD HH:mm");

        momentDate.add(adding, "minutes");

        if (dayOffset !== undefined) {
            momentDate.add(dayOffset, "days");
        } else if (submode.value === "0" && momentDate.hours() < 13 && momentDate.date() === 1) {
            momentDate.add(1, "days");
        }

        return momentDate.toDate();
    }
}

const mapStateToProps = (state, ownProps) => {
    return {
        validationSummary: state.project.validationSummary,
        opmodesSelected: state.driver.opmodesSelected,
        projectLocation: state.project.location,
        issues: state.project.issueByProperty,
        midnightShift: state.project.midnightShift,
        astroSavingsValue: state.driver.astroSavingsValue,
        factoryDefaults: state.driver.factoryDefaults,
    };
};

const mapDispatchToProps = {
    fetchDriverOpmodesSelected,
    fetchDriverProperties,
    fetchAstroSavings,
    fetchDriverDefaults,
};

export default connect(mapStateToProps, mapDispatchToProps)(AstroDim5);
